/**
 * Everything the tree needs, in one place.
 *
 * React Query owns server state; nothing else caches a response. Two caches would
 * disagree, and the one an analyst is looking at would be the stale one.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import type { ReactElement, ReactNode } from 'react';
import { SessionProvider } from '@/features/auth/session';
import { ApiError } from '@/lib/api';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 or a 403 is an answer, not a transient failure. Retrying either produces
      // a burst of denials in the audit log and tells the analyst nothing new.
      retry: (failureCount, error) =>
        error instanceof ApiError && (error.status === 401 || error.status === 403)
          ? false
          : failureCount < 2,
      staleTime: 15_000,
      refetchOnWindowFocus: false,
    },
  },
});

/** Wrap the application in its providers. */
export function Providers({ children }: { children: ReactNode }): ReactElement {
  return (
    <QueryClientProvider client={client}>
      <BrowserRouter>
        <SessionProvider>{children}</SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
